// HOOKS

import useColumns from "../../hooks/other/useColumns.other.hook";
import useSelectAll from "../../hooks/other/useSelectAll.other.hook";
import { useContext, useEffect } from "react";

// CONTEXTS
import { TicketsContext } from "../../contexts/tickets/tickets.context";
import { SelectedTicketsContext } from "../../contexts/tickets/selectedTickets.context";

// COMPONENTS
import QueryContainer from "../../components/tables/query/queryContainer.table.component";
import PaginationBar from "../../components/tables/customizableTable/features/pagination/paginationBar";
import Refresh from "../../components/refresh/refresh.component";
import Spinner from "../../components/spinner/spinner.component";
import { ticketColumns } from "../../assets/variables/columns/ticket.columns";
import Table from "../../components/tables/customizableTable/table.customizableTable";
import GetTicketFormProps from "../../components/formProps/tickets/ticket.formProp";
import TicketStatusPresetDropdown from "../../components/dropdowns/presets/ticketStatus.preset.dropdown";
// ** TicketActionBar
import TicketInfoBar from "../../components/infoBar/tickets/ticket.infoBar";

const CLASSNAMES = {
  pagination: {
    arrow:
      "active:shadow-inner active:shadow-black bg-lightPrimary hover:bg-lightSecondary shadow shadow-black hover:cursor-pointer hover:dark:bg-darkSecondary dark:bg-darkPrimary dark:fill-white rounded-md w-8 h-8",
    currentPage:
      "shadow-inner shadow-black dark:bg-darkSecondary bg-lightSecondary dark:text-white text-white hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
    page: "active:shadow-inner active:shadow-black shadow shadow-black hover:dark:bg-darkSecondary hover:bg-lightSecondary active:shadow-inner dark:text-white text-white dark:bg-darkPrimary bg-lightPrimary hover:cursor-pointer rounded h-8 w-8 flex items-center justify-center",
  },
  columnFilter: {
    dropdownButton: {
      dropdown:
        "border-animate rounded h-[250px] w-[250px] overflow-y-scroll absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  limit: {
    dropdownButton: {
      dropdown:
        "border-animate rounded absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
  spinner: {
    container: "flex w-full h-full justify-center items-center",
    spinner:
      "shadow animate-spin border-dashed rounded-full h-16 w-16 border-2 border-primary",
  },
  statusFilter: {
    dropdownButton: {
      dropdown:
        "w-[225px] border-animate rounded overflow-y-auto absolute backdrop-blur-sm bg-greenGlass flex flex-col gap-1 p-2 top-11 z-50",
    },
  },
};

const TicketsPage = () => {
  const {
    selectedTickets,
    setSelectedTickets,
    resetSelectedTickets,
    removeSelectedTickets,
    addSelectedTickets,
  } = useContext(SelectedTicketsContext);

  const { selectAll, handleSelectAll, resetSelectAll, setSelectAll } =
    useSelectAll();
  const { columns, handleColumnFilterCheck, handleResize } =
    useColumns(ticketColumns);
  const {
    tickets,
    metaData,
    getTickets,
    query,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    handleSort,
    isLoading,
    handleChange,
    ticketInput,
    setTicketInput,
  } = useContext(TicketsContext);
  const { limit, page, search, sort } = query;

  // Fetch tickets on page load
  useEffect(() => {
    getTickets();
  }, []);
  // Reset selectAll when selected changes
  useEffect(() => {
    if (selectedTickets?.length === 0) resetSelectAll();
  }, [selectedTickets]);

  // Check if all tickets are selected
  useEffect(() => {
    if (tickets?.length === 0) return;
    // Create set of tickets and selected tickets
    const checkSet = new Set([
      ...tickets.map((ticket) => ticket._id),
      ...selectedTickets.map((ticket) => ticket._id),
    ]);

    // Check if all tickets are selected
    if (checkSet.size !== selectedTickets.length) {
      setSelectAll(false);
    } else {
      setSelectAll(true);
    }
  }, [tickets]);

  // Add/Remove tickets from selected
  const selectAllHandler = () => {
    const updatedSeletedTickets = handleSelectAll(
      selectedTickets,
      tickets,
      "_id"
    );
    setSelectedTickets(updatedSeletedTickets);
  };

  const handleSelect = (event) => {
    const { _id } = event.target.dataset;
    const existingSelection = selectedTickets?.find((item) => item._id === _id);
    if (existingSelection) {
      removeSelectedTickets(_id);
    } else {
      addSelectedTickets(tickets.find((item) => item._id === _id));
    }
  };

  const changeHandlers = {
    selectAllHandler,
    handleSelect,
    handleChange,
    handleMultiSelectChange: () => {},
    handleSearch,
  };
  return (
    <div
      style={{ height: "calc(100vh - 136px)" }}
      className="p-4 flex flex-col gap-2 overflow-y-hidden"
    >
      <h1 className="font-ubuntuBold text-lg items-center text-center">
        Tickets
      </h1>
      <div className="flex flex-row justify-between">
        <QueryContainer
          queryProps={{
            search: {
              search,
              handleChange: handleSearch,
            },
            limit: {
              handleChange: handleLimitChange,
              limit,
            },
            columnFilter: {
              handleChange: handleColumnFilterCheck,
              columns,
            },
          }}
          classNames={{
            limit: CLASSNAMES.limit,
            columnFilter: CLASSNAMES.columnFilter,
          }}
        >
          <TicketStatusPresetDropdown
            formFields={ticketInput}
            setFormFields={setTicketInput}
            classNames={CLASSNAMES.statusFilter}
          />
        </QueryContainer>
        {/* <TicketActionBar /> */}
      </div>
      <div className="p-2">
        <PaginationBar
          metaData={metaData}
          currentPage={page}
          totalPages={metaData?.totalPages || 1}
          handleChange={handlePageChange}
          classNames={CLASSNAMES.pagination}
        />
      </div>
      <div className="flex flex-row justify-between">
        <TicketInfoBar
          metaData={metaData}
          selectedTagProps={{
            handleClear: resetSelectedTickets,
            label: `Tickets: ${selectedTickets?.length || 0}`,
          }}
        />
        <Refresh minimumValue={30} handleEvent={getTickets} />
      </div>

      <div className="flex items-center justify-start overflow-hidden w-full h-full">
        <div
          className={`rounded relative ${
            isLoading ? "w-full" : "overflow-x-auto"
          } h-full px-1 pb-8`}
        >
          {(isLoading || !tickets?.length > 0) && (
            <div className=" shadow shadow-black rounded z-20 bg-gray-300/35 backdrop-blur-sm absolute w-full h-full flex justify-center items-center">
              <Spinner classNames={CLASSNAMES.spinner} />
            </div>
          )}
          <Table
            metaData={metaData}
            columnProps={{ columns, handleResize }}
            sortProps={{ sort, handleSort }}
            options={{ select: true }}
            changeHandlers={changeHandlers}
            getFormProps={GetTicketFormProps}
            defaultColumns={ticketColumns}
            data={tickets}
            select={true}
            selectAll={selectAll}
            selectedItems={selectedTickets}
          />
        </div>
      </div>
    </div>
  );
};

export default TicketsPage;
